import { useTranslation } from "react-i18next";
import { Layout } from "@/components/Layout";
import { CONTACT } from "@/lib/contact";

const LAST_UPDATED = "9 de octubre de 2026";

export default function Terms() {
  const { t, i18n } = useTranslation();
  const isSpanish = i18n.language === "es";
  return (
    <Layout>
      <section className="border-b border-as-border bg-as-black">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <p className="as-label text-as-green mb-3">{t("terms.eyebrow")}</p>
          <h1 className="as-heading text-4xl sm:text-6xl text-white leading-tight" data-testid="terms-title">
            {t("terms.title_1")} <span className="text-as-green">{t("terms.title_2")}</span>
          </h1>
          <p className="text-as-gray mt-4 max-w-2xl text-sm">
            {t("terms.last_updated_label")} <span className="text-white">{LAST_UPDATED}</span>
          </p>
          {!isSpanish && (
            <p
              className="mt-4 max-w-2xl text-xs text-as-gray-dim border-l-2 border-as-green pl-3"
              data-testid="terms-translation-notice"
            >
              {t("terms.note_es_source")}
            </p>
          )}
        </div>
      </section>

      <article className="bg-as-surface py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 space-y-10 text-as-gray leading-relaxed">
          <Section title={t("terms.s1_title")}>
            <p>
              Bienvenido a <b className="text-white">AS TRAINING PRO</b> (en adelante, "la Plataforma", "el Servicio" o "nosotros"),
              una plataforma digital de entrenamiento personal, nutrición y seguimiento operada por{" "}
              <b className="text-white">Fabricio Alves de Almeida</b>, con sede en Miami, Florida, Estados Unidos.
            </p>
            <p>
              Estos Términos de Uso (en adelante, "Términos") regulan tu acceso y uso del sitio web{" "}
              <a href="https://www.astraining2020.com" className="text-as-green">www.astraining2020.com</a>, la aplicación móvil y
              todos los servicios asociados. Al registrarte, acceder o utilizar la Plataforma aceptas estos Términos
              en su totalidad. Si no estás de acuerdo con alguno de los apartados, por favor no utilices el Servicio.
            </p>
          </Section>

          <Section title={t("terms.s2_title")}>
            <p>
              Para usar AS TRAINING PRO debes tener al menos <b className="text-white">18 años cumplidos</b> o la mayoría
              de edad legal en tu jurisdicción. Al registrarte declaras y garantizas que: (a) eres mayor de edad; (b) tienes
              capacidad legal para aceptar estos Términos; (c) la información que proporcionas es verdadera, precisa y actual; y
              (d) eres responsable de mantener la confidencialidad de tus credenciales de acceso.
            </p>
            <p>
              No ofrecemos servicios a menores de edad. Si detectamos que un menor se ha registrado, procederemos a eliminar su
              cuenta y sus datos conforme a nuestra <a href="/privacy" className="text-as-green">Política de Privacidad</a>.
            </p>
          </Section>

          <Section title={t("terms.s3_title")}>
            <p>
              AS TRAINING PRO proporciona, entre otros: planes personalizados de entrenamiento físico, guías nutricionales,
              calculadoras de macronutrientes, biblioteca de ejercicios y suplementación, seguimiento de progreso corporal,
              comunicación con el coach vía WhatsApp y descarga de materiales en PDF.
            </p>
            <p>
              El contenido se basa en principios generales de entrenamiento y nutrición respaldados por evidencia científica,
              adaptados a la información que tú proporcionas en tu evaluación inicial. <b className="text-white">No sustituye</b>{" "}
              la consulta con profesionales médicos, nutricionistas colegiados o fisioterapeutas.
            </p>
          </Section>

          <Section title={t("terms.s4_title")}>
            <p className="text-white font-bold">
              Antes de iniciar cualquier programa de ejercicio o alimentación, consulta con tu médico de cabecera,
              especialmente si tienes alguna de las siguientes condiciones:
            </p>
            <ul className="list-disc list-inside space-y-1 marker:text-as-green">
              <li>Enfermedades cardiovasculares, hipertensión no controlada o arritmias</li>
              <li>Diabetes tipo 1 o tipo 2</li>
              <li>Lesiones musculoesqueléticas, hernias o cirugías recientes</li>
              <li>Trastornos alimentarios diagnosticados o en recuperación</li>
              <li>Embarazo, postparto o lactancia</li>
              <li>Cualquier otra condición médica crónica o aguda</li>
            </ul>
            <p>
              <b className="text-white">AS TRAINING PRO no proporciona consejo médico.</b> Los planes de entrenamiento, nutrición y
              suplementación son recomendaciones generales de carácter informativo. El uso de los contenidos es bajo tu propia
              responsabilidad y riesgo. En caso de malestar, dolor o cualquier síntoma anormal durante el entrenamiento, detente
              inmediatamente y consulta a un profesional de la salud.
            </p>
          </Section>

          <Section title={t("terms.s5_title")}>
            <p>
              AS TRAINING PRO ofrece 3 planes de suscripción mensual (AS START, AS EVOLUTION y AS ELITE) procesados a través de
              <b className="text-white"> Stripe, Inc.</b> para pagos con tarjeta de crédito/débito, o manualmente vía Zelle o Venmo.
              En aplicaciones móviles distribuidas por Apple App Store o Google Play, las suscripciones son gestionadas por{" "}
              <b className="text-white">RevenueCat, Inc.</b> y procesadas por las tiendas correspondientes.
            </p>
            <p>
              <b className="text-white">Renovación automática:</b> todas las suscripciones se renuevan automáticamente al final de
              cada período de facturación (mensual) salvo que las canceles antes de la fecha de renovación. El cargo se realizará
              al método de pago registrado.
            </p>
            <p>
              <b className="text-white">Cancelación:</b> puedes cancelar tu suscripción en cualquier momento desde:
            </p>
            <ul className="list-disc list-inside space-y-1 marker:text-as-green">
              <li>Tu panel de usuario (sección "Mi plan")</li>
              <li>Tu cuenta de App Store (iOS) o Google Play (Android) si la contrataste desde la app</li>
              <li>Contactando a soporte vía <a href={`mailto:${CONTACT.email}`} className="text-as-green">{CONTACT.email}</a></li>
            </ul>
            <p>
              La cancelación tendrá efecto al final del período ya pagado. Mantendrás acceso al contenido premium hasta esa fecha.
            </p>
            <p>
              <b className="text-white">Reembolsos:</b> dado que la Plataforma entrega contenido digital de acceso inmediato,
              los pagos realizados <b className="text-white">no son reembolsables</b> una vez iniciado el período de servicio,
              excepto cuando lo exija la ley aplicable o cuando sea gestionado directamente por las tiendas (Apple/Google) según
              sus propias políticas. Para solicitudes especiales, contáctanos por email.
            </p>
            <p>
              <b className="text-white">Cambios de precio:</b> podemos modificar los precios de las suscripciones. Si lo hacemos,
              te notificaremos con al menos <b className="text-white">30 días de antelación</b> por correo electrónico. Los cambios
              aplicarán en tu próximo ciclo de renovación.
            </p>
          </Section>

          <Section title={t("terms.s6_title")}>
            <p>
              Todo el contenido disponible en AS TRAINING PRO (textos, rutinas, pósters, videos, PDFs, logotipos, código fuente,
              fotografías, música e identidad visual) es propiedad exclusiva de <b className="text-white">Fabricio Alves de Almeida</b>{" "}
              o se utiliza bajo licencia. Está protegido por las leyes de propiedad intelectual de Estados Unidos y tratados
              internacionales.
            </p>
            <p>Al suscribirte obtienes una <b className="text-white">licencia personal, limitada, no exclusiva, no transferible y revocable</b> para:</p>
            <ul className="list-disc list-inside space-y-1 marker:text-as-green">
              <li>Acceder y usar el contenido para tu entrenamiento personal</li>
              <li>Descargar los PDFs que te correspondan según tu plan activo</li>
              <li>Imprimir una copia física para uso personal, no comercial</li>
            </ul>
            <p>Queda <b className="text-white">expresamente prohibido</b>:</p>
            <ul className="list-disc list-inside space-y-1 marker:text-as-green">
              <li>Compartir tus credenciales de acceso con terceros</li>
              <li>Redistribuir, revender, sublicenciar o publicar el contenido</li>
              <li>Reproducir el contenido en redes sociales, blogs, canales de YouTube u otras plataformas sin autorización escrita</li>
              <li>Utilizar el contenido para formar, entrenar o asesorar a otras personas con fines comerciales</li>
              <li>Realizar ingeniería inversa, descompilar o extraer código fuente de la Plataforma</li>
            </ul>
          </Section>

          <Section title={t("terms.s7_title")}>
            <p>Al usar la Plataforma te comprometes a NO:</p>
            <ul className="list-disc list-inside space-y-1 marker:text-as-green">
              <li>Realizar actividades ilegales, fraudulentas o que infrinjan derechos de terceros</li>
              <li>Interferir con la seguridad o funcionamiento normal del Servicio</li>
              <li>Acceder sin autorización a cuentas, servidores o redes de AS TRAINING PRO</li>
              <li>Subir contenido ofensivo, difamatorio, violento, pornográfico o discriminatorio</li>
              <li>Suplantar la identidad de otra persona o entidad</li>
              <li>Usar bots, scrapers o cualquier medio automatizado no autorizado</li>
              <li>Transmitir virus, malware o cualquier código malicioso</li>
            </ul>
            <p>
              Nos reservamos el derecho de <b className="text-white">suspender o cancelar tu cuenta</b> sin reembolso ante
              cualquier violación de estos Términos, previa notificación cuando sea razonablemente posible.
            </p>
          </Section>

          <Section title={t("terms.s8_title")}>
            <p>
              AS TRAINING PRO se proporciona <b className="text-white">"TAL CUAL"</b> y "SEGÚN DISPONIBILIDAD", sin garantías
              expresas o implícitas de ningún tipo, incluyendo —pero sin limitarse a— garantías de comerciabilidad, idoneidad
              para un propósito particular o no infracción.
            </p>
            <p>No garantizamos que:</p>
            <ul className="list-disc list-inside space-y-1 marker:text-as-green">
              <li>El Servicio esté libre de errores, interrupciones o fallos técnicos</li>
              <li>Los resultados físicos, estéticos o de rendimiento sean idénticos a los testimonios mostrados</li>
              <li>El contenido sea apropiado para toda condición física o médica individual</li>
            </ul>
            <p>
              Los resultados dependen de múltiples factores personales como genética, consistencia, descanso, estrés, condiciones
              médicas previas y adherencia al plan. <b className="text-white">Los testimonios y transformaciones mostradas son
              casos reales pero no constituyen una promesa de resultado.</b>
            </p>
          </Section>

          <Section title={t("terms.s9_title")}>
            <p>
              En la máxima medida permitida por la ley aplicable, <b className="text-white">AS TRAINING PRO, su propietario,
              empleados y colaboradores no serán responsables</b> por daños directos, indirectos, incidentales, especiales,
              consecuentes o punitivos, incluyendo pero sin limitarse a:
            </p>
            <ul className="list-disc list-inside space-y-1 marker:text-as-green">
              <li>Lesiones físicas, dolor o problemas de salud derivados del uso de los contenidos</li>
              <li>Pérdida de ingresos, ahorros, datos o oportunidades</li>
              <li>Interrupciones del Servicio o pérdida de contenido descargado</li>
              <li>Daños causados por terceros, proveedores o integraciones (Stripe, RevenueCat, Google, Apple, WhatsApp)</li>
            </ul>
            <p>
              Nuestra responsabilidad total agregada, en cualquier caso, no excederá del <b className="text-white">importe pagado
              por ti en los 12 meses anteriores</b> al hecho que origine la reclamación.
            </p>
          </Section>

          <Section title={t("terms.s10_title")}>
            <p>
              Aceptas <b className="text-white">indemnizar, defender y mantener indemne</b> a AS TRAINING PRO, Fabricio Alves de
              Almeida y sus colaboradores frente a cualquier reclamación, pérdida, responsabilidad, gasto o daño (incluyendo
              honorarios legales razonables) derivados de: (a) tu uso del Servicio; (b) tu violación de estos Términos; (c)
              tu violación de derechos de un tercero; o (d) la información inexacta que hayas proporcionado.
            </p>
          </Section>

          <Section title={t("terms.s11_title")}>
            <p>
              El tratamiento de tus datos personales se rige por nuestra{" "}
              <a href="/privacy" className="text-as-green">Política de Privacidad</a>, que forma parte integral de estos Términos.
              Puedes solicitar la eliminación total de tu cuenta y datos en cualquier momento desde{" "}
              <a href="/delete-account" className="text-as-green">astraining2020.com/delete-account</a>.
            </p>
          </Section>

          <Section title={t("terms.s12_title")}>
            <p>
              Nos reservamos el derecho de <b className="text-white">modificar estos Términos</b> en cualquier momento.
              Los cambios materiales serán notificados por correo electrónico con al menos 30 días de antelación y publicados en
              esta misma página con la fecha de actualización. El uso continuado del Servicio después de la fecha de entrada en
              vigor constituye tu aceptación de los Términos modificados.
            </p>
          </Section>

          <Section title={t("terms.s13_title")}>
            <p>
              Estos Términos se rigen por las leyes del <b className="text-white">Estado de Florida, Estados Unidos</b>, sin
              atender a conflictos de leyes. Cualquier disputa derivada o relacionada con estos Términos o con el Servicio
              se someterá a la jurisdicción exclusiva de los tribunales estatales o federales ubicados en el Condado de
              Miami-Dade, Florida.
            </p>
            <p>
              Si eres residente de la Unión Europea, Reino Unido, Brasil u otra jurisdicción con leyes de protección al consumidor
              más favorables, nada en estos Términos limitará los derechos irrenunciables que te otorgue tu legislación local.
            </p>
          </Section>

          <Section title={t("terms.s14_title")}>
            <p>
              Si alguna disposición de estos Términos se declara inválida o inaplicable por un tribunal competente, las
              disposiciones restantes seguirán en pleno vigor y efecto. La invalidez parcial no afectará la validez general
              del resto de los Términos.
            </p>
            <p>
              El hecho de que no ejerzamos un derecho o disposición de estos Términos no constituirá una renuncia a dicho
              derecho o disposición.
            </p>
          </Section>

          <Section title={t("terms.s15_title")}>
            <p>Para preguntas, reclamaciones o cualquier asunto relacionado con estos Términos:</p>
            <ul className="list-disc list-inside space-y-1 marker:text-as-green">
              <li>
                <b className="text-white">Responsable:</b> Fabricio Alves de Almeida — AS TRAINING PRO
              </li>
              <li>
                <b className="text-white">Email:</b>{" "}
                <a href={`mailto:${CONTACT.email}`} className="text-as-green">{CONTACT.email}</a>
              </li>
              <li>
                <b className="text-white">WhatsApp:</b>{" "}
                <a
                  href={`https://wa.me/${CONTACT.phoneIntl}`}
                  target="_blank"
                  rel="noreferrer"
                  className="text-as-green"
                >
                  {CONTACT.phone}
                </a>
              </li>
              <li>
                <b className="text-white">Ubicación:</b> Miami, Florida, Estados Unidos
              </li>
            </ul>
            <p className="pt-4 text-xs text-as-gray-dim">
              Al continuar usando AS TRAINING PRO confirmas que has leído, entendido y aceptado estos Términos de Uso.
            </p>
          </Section>
        </div>
      </article>
    </Layout>
  );
}

function Section({ title, children }) {
  const id = title.toLowerCase().replace(/[^a-z0-9]+/g, "-");
  return (
    <section aria-labelledby={`terms-${id}`} data-testid={`terms-section-${id}`}>
      <h2
        id={`terms-${id}`}
        className="as-heading text-2xl sm:text-3xl text-white mb-3 border-l-4 border-as-green pl-4"
      >
        {title}
      </h2>
      <div className="space-y-3 text-sm sm:text-base">{children}</div>
    </section>
  );
}
